<template>
  <div
    class="itf-verify-page"
    :class="'lang-' + lang"
  >
    <div
      v-if="loading"
      class="itf-verify-state"
    >
      {{ t.loading }}
    </div>

    <div
      v-else-if="error"
      class="itf-verify-state itf-verify-state--error"
    >
      {{ errorMessage }}
    </div>

    <div
      v-else-if="data"
      class="itf-verify-content"
    >
      <div class="itf-verify-header">
        <img
          class="itf-verify-emblem"
          :src="emblemSrc"
          alt=""
        >
        <h1 class="itf-verify-ministry">
          {{ t.ministryTitle }}
        </h1>
      </div>

      <div class="itf-verify-divider">
        <span class="itf-verify-divider-line" />
        <span class="itf-verify-divider-diamond" />
        <span class="itf-verify-divider-line" />
      </div>

      <div class="itf-verify-cert-no">
        {{ t.certificateNoLabel }} <strong>{{ certificateNoDisplay }}</strong>
      </div>

      <div class="itf-verify-card">
        <div class="itf-verify-row">
          <div class="itf-verify-icon">
            <img
              :src="plateIconSrc"
              alt=""
            >
          </div>
          <div class="itf-verify-row-text">
            <div class="itf-verify-label">
              {{ t.registrationNumber }}
            </div>
            <div class="itf-verify-value">
              {{ data.CarRegNo || '—' }}
            </div>
          </div>
        </div>

        <div class="itf-verify-row">
          <div class="itf-verify-icon">
            <img
              :src="truckIconSrc"
              alt=""
            >
          </div>
          <div class="itf-verify-row-text">
            <div class="itf-verify-label">
              {{ t.makeModel }}
            </div>
            <div class="itf-verify-value">
              {{ makeModelDisplay }}
            </div>
          </div>
        </div>

        <div class="itf-verify-row itf-verify-row--last">
          <div class="itf-verify-icon">
            <img
              :src="calendarIconSrc"
              alt=""
            >
          </div>
          <div class="itf-verify-row-text">
            <div class="itf-verify-label">
              {{ t.validUntil }}
            </div>
            <div class="itf-verify-value">
              {{ validUntilDate }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios'

const TEXTS = {
  en: {
    loading: 'Loading certificate…',
    notFound: 'Certificate not found.',
    loadError: 'Unable to load certificate.',
    ministryTitle: 'Ministry of Territorial Administration and Infrastructure of the Republic of Armenia',
    certificateNoLabel: 'ECMT Certificate No:',
    registrationNumber: 'Registration Number',
    makeModel: 'Make/Model',
    validUntil: 'The Certificate is valid',
  },
  hy: {
    loading: 'Վկայականի տվյալները բեռնվում են…',
    notFound: 'Վկայականը չի գտնվել։',
    loadError: 'Չհաջողվեց բեռնել վկայականը։',
    ministryTitle: 'ՀՀ տարածքային կառավարման և ենթակառուցվածքների նախարարություն',
    certificateNoLabel: 'ՏՆԵԿ վկայական №',
    registrationNumber: 'Գրանցման համարանիշ',
    makeModel: 'Մակնիշ/մոդել',
    validUntil: 'Վկայականը գործում է',
  },
}

export default {
  name: 'ItfVerify',
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '',
      data: null,
      emblemSrc: require('@/assets/images/itf/coats-of-arms.svg'),
      plateIconSrc: require('@/assets/images/itf/icon-plate.svg'),
      truckIconSrc: require('@/assets/images/itf/icon-truck.svg'),
      calendarIconSrc: require('@/assets/images/itf/icon-calendar.svg'),
    }
  },
  computed: {
    lang() {
      const queryLang = (this.$route.query.lang || '').toLowerCase()
      return queryLang === 'hy' ? 'hy' : 'en'
    },
    t() {
      return TEXTS[this.lang] || TEXTS.en
    },
    certificateNoDisplay() {
      if (!this.data || this.data.certificateNo == null) return '—'
      return String(this.data.certificateNo)
    },
    makeModelDisplay() {
      if (!this.data) return '—'
      return [this.data.car, this.data.car_model].filter(Boolean).join(' ') || '—'
    },
    validUntilDate() {
      if (!this.data || !this.data.createdAt) return '—'
      const date = new Date(this.data.createdAt)
      const next = Number(this.data.NextCheckup)
      if (next === 0) {
        date.setMonth(date.getMonth() + 6)
      } else if (next === 2) {
        date.setFullYear(date.getFullYear() + 2)
      } else {
        date.setFullYear(date.getFullYear() + 1)
      }
      return this.formatDate(date)
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler() {
        this.fetchCertificate()
      },
    },
  },
  methods: {
    async fetchCertificate() {
      this.loading = true
      this.error = false
      this.data = null

      try {
        const response = await axios.get(`itf/verify/${this.$route.params.id}`)
        if (response.data.error || !response.data.data) {
          this.error = true
          this.errorMessage = response.data.message || this.t.notFound
          return
        }
        this.data = response.data.data
      } catch (e) {
        this.error = true
        if (e.response && e.response.status === 404) {
          this.errorMessage = this.t.notFound
        } else {
          this.errorMessage = this.t.loadError
        }
      } finally {
        this.loading = false
      }
    },
    formatDate(value) {
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return '—'
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}.${month}.${year}`
    },
  },
}
</script>

<style lang="scss" scoped>
.itf-verify-page {
  min-height: 100vh;
  background: #f4f7fb;
  color: #0a2f6b;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.itf-verify-state {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  font-size: 16px;
  color: #0a2f6b;

  &--error {
    color: #b42318;
  }
}

.itf-verify-content {
  max-width: 420px;
  margin: 0 auto;
  padding: 32px 20px 40px;
}

.itf-verify-header {
  text-align: center;
}

.itf-verify-emblem {
  width: 72px;
  height: auto;
  display: block;
  margin: 0 auto 16px;
}

.itf-verify-ministry {
  margin: 0;
  font-size: 16px;
  line-height: 1.35;
  font-weight: 700;
  color: #0a2f6b;
}

.itf-verify-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 22px 0 18px;
}

.itf-verify-divider-line {
  flex: 1;
  max-width: 120px;
  height: 1px;
  background: linear-gradient(90deg, transparent, #8aa8cf, transparent);
}

.itf-verify-divider-diamond {
  width: 8px;
  height: 8px;
  background: #0a2f6b;
  transform: rotate(45deg);
  flex-shrink: 0;
}

.itf-verify-cert-no {
  text-align: center;
  font-size: 15px;
  line-height: 1.4;
  margin-bottom: 22px;
  color: #0a2f6b;

  strong {
    font-weight: 700;
  }
}

.itf-verify-card {
  background: #edf2f8;
  border-radius: 16px;
  padding: 8px 16px;
}

.itf-verify-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(10, 47, 107, 0.08);

  &--last {
    border-bottom: 0;
  }
}

.itf-verify-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #dce7f5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 24px;
    height: 24px;
    display: block;
  }
}

.itf-verify-row-text {
  min-width: 0;
  flex: 1;
}

.itf-verify-label {
  font-size: 13px;
  line-height: 1.3;
  color: #5f7394;
  margin-bottom: 4px;
}

.itf-verify-value {
  font-size: 16px;
  line-height: 1.35;
  font-weight: 700;
  color: #0a2f6b;
  word-break: break-word;
}

.lang-hy {
  .itf-verify-ministry {
    font-size: 15px;
  }
}
</style>
